import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { addDays, format, isBefore, isValid, parse, set, startOfDay } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getStaff } from 'contexts/accounts/actions';
import { Account, Staff } from 'contexts/accounts/types';
import { fetchVoucherSummary } from 'contexts/voucher/services';
import { resolveAccountPricingPlan } from 'contexts/pricingPlans/identity';
import { PricingPlan } from 'contexts/pricingPlans/types';
import { SessionContext } from 'contexts/session';
import { ACCOUNT_MANAGEMENT_MODAL_COPY, DATE_FORMAT } from 'constants/accounts';
import { MAX_LICENSES_ACCOUNT } from 'constants/licenses';
import { ADDON_SKILL_DIVE, isSkillDiveSupportedPlan } from 'constants/plans_config';
import Button from 'components/button';
import Checkbox from 'components/checkbox';
import DatePicker from 'components/datepicker';
import Icon from 'components/icons';
import InputSelect from 'components/inputs/inputSelect';
import Modal, { ConfirmModal } from 'components/modal';
import Notification from 'components/notification';
import {
  AccountAddonPayload,
  changeAccountPlan,
  createAccount,
  editAccount as editAccountService,
  loadActiveCertifications,
  reactivateAccount,
  updateEnabledExams,
  updateVoucherBalance,
} from 'services/accounts';
import EnterprisePlanLogo from 'assets/images/icon-enterprise.svg';
import EnterprisePlusLabExperiencePlanLogo from 'assets/images/icon-enterprise-plus-lab.svg';
import LabExperiencePlanLogo from 'assets/images/icon-lab-experiences-plan.svg';
import ProfessionalPlanLogo from 'assets/images/icon-professional.svg';
import { isMobile } from 'react-device-detect';
import {
  AccountNameField,
  AccountNameInput,
  AccountNameLabel,
  Actions,
  AddonCheckbox,
  AddonControls,
  AddonCounter,
  AddonCounterButton,
  AddonCounterValue,
  AddonDetails,
  AddonEntry,
  AddonLabel,
  AddonList,
  AddonName,
  AddonOption,
  Addons,
  AddonsTitle,
  AdvancedOptionsButton,
  CertificationCode,
  CertificationDescription,
  CertificationOption,
  CertificationOptions,
  CertificationOptionsTitle,
  CertificationSelectionStatus,
  CloseButton,
  Content,
  DateCard,
  DateCardLabel,
  DateFields,
  DateFlags,
  DateHeader,
  DatePickerControl,
  DateSection,
  DateTitle,
  FreeTrial,
  FreeTrialLabel,
  Header,
  LegacyBadge,
  ModalShell,
  PlanChangeDisclaimer,
  PlanIcon,
  PlanLegend,
  PlanLicenseControls,
  PlanName,
  PlanOption,
  PlanOptions,
  PlanRadio,
  PlanTitle,
  PlanUnlimited,
  StaffField,
  StaffFieldLabel,
  StaffFields,
  Title,
  UnlimitedLabel,
} from './styles';

export interface AccountManagementModalProps {
  managePlan: boolean;
  editAccount: boolean;
  onClose: (result?: 'ok' | 'error') => void;
  account?: Account;
  isVisible: boolean;
  onAccountEdition?: (account: Account) => void;
}

const PLAN_LOGOS: Record<string, string> = {
  enterprise: EnterprisePlanLogo,
  'enterprise-plus-lab-experience': EnterprisePlusLabExperiencePlanLogo,
  'lab-experience': LabExperiencePlanLogo,
  pro: ProfessionalPlanLogo,
  professional: ProfessionalPlanLogo,
};

const INE_CERTIFICATIONS_ADDON = {
  id: 'ine-certifications',
  name: 'INE Certifications',
  quantityLabel: ACCOUNT_MANAGEMENT_MODAL_COPY.vouchersLabel,
};

const PROFESSIONAL_PLAN_CODES = new Set(['pro', 'professional']);
const ADDON_TRANSITION_DURATION = 280;

type AddonTransitionState = 'entering' | 'leaving' | 'visible';

interface ModalAddon {
  id: string;
  name: string;
  quantityLabel: string;
}

interface CertificationOptionData {
  id: string;
  code: string;
  name: string;
}

const getErrorDetail = (error: unknown): string | undefined => {
  const asRecord = (value: unknown): Record<string, unknown> | undefined =>
    value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
  const getUsableString = (value: unknown): string | undefined => {
    if (typeof value === 'string' && value.trim()) return value;
    if (Array.isArray(value)) {
      return value.map(getUsableString).find((item): item is string => Boolean(item));
    }

    return undefined;
  };
  const root = asRecord(error);
  const response = asRecord(root?.response);
  const data = asRecord(response?.data);
  const detailsValue = data?.details ?? root?.details;
  const details = asRecord(detailsValue);
  const candidates = [
    details?.start_date,
    details?.initial_voucher_balance,
    details?.enabled_certification_version_ids,
    details?.detail,
    data?.detail,
    detailsValue,
    root?.detail,
    root?.message,
    error,
  ];

  return candidates.map(getUsableString).find((item): item is string => Boolean(item));
};

const SKILL_DIVE_ADDON_CODES = new Set(['skill-dive', ADDON_SKILL_DIVE]);

const isSkillDiveAddon = ({ code, name }: { code: string; name: string }): boolean =>
  SKILL_DIVE_ADDON_CODES.has(code) || name === 'Skill Dive';

const getPlanAddons = (plan?: PricingPlan, includeCertifications = true): ModalAddon[] => {
  if (!plan) return [];

  const includesCertifications =
    PROFESSIONAL_PLAN_CODES.has(plan.code) || plan.code === 'enterprise';
  const catalogAddons = plan.addons.filter(
    (addon) => !isSkillDiveAddon(addon) || isSkillDiveSupportedPlan(plan.code)
  );

  return [
    ...catalogAddons.map((addon) => ({
      ...addon,
      quantityLabel: ACCOUNT_MANAGEMENT_MODAL_COPY.licensesLabel,
    })),
    ...(includeCertifications && includesCertifications ? [INE_CERTIFICATIONS_ADDON] : []),
  ];
};

const getExistingAddons = (account?: Account): ModalAddon[] =>
  (account?.account_addons?.addons || []).map(({ id, name }) => ({
    id,
    name,
    quantityLabel: ACCOUNT_MANAGEMENT_MODAL_COPY.licensesLabel,
  }));

const getAddonDisplayOrder = (addon: ModalAddon): number => {
  if (addon.name === 'Skill Dive') return 0;
  if (addon.id === INE_CERTIFICATIONS_ADDON.id) return 1;

  return 2;
};

const mergeAddons = (planAddons: ModalAddon[], existingAddons: ModalAddon[]): ModalAddon[] =>
  [
    ...planAddons,
    ...existingAddons.filter(
      (existingAddon) => !planAddons.some((planAddon) => planAddon.id === existingAddon.id)
    ),
  ]
    .map((addon, index) => ({ addon, index }))
    .sort(
      ({ addon: firstAddon, index: firstIndex }, { addon: secondAddon, index: secondIndex }) =>
        getAddonDisplayOrder(firstAddon) - getAddonDisplayOrder(secondAddon) ||
        firstIndex - secondIndex
    )
    .map(({ addon }) => addon);

const getStaffOptions = (staff: Staff[], role: 'Support' | 'Sales') =>
  staff
    .filter(({ staff_role }) => staff_role === role)
    .map(({ first_name, id, last_name }) => ({
      label: `${first_name} ${last_name}`,
      value: id,
    }))
    .sort((first, second) => `${first.label}`.localeCompare(`${second.label}`));

const getStaffName = (staff: Staff[], value?: string | number): string | null => {
  const selected = staff.find(
    ({ first_name, id, last_name }) => id === value || value === `${first_name} ${last_name}`
  );

  return selected ? `${selected.first_name} ${selected.last_name}` : null;
};

const getStaffId = (
  staff: Staff[],
  value: string | number | undefined,
  role: 'Support' | 'Sales'
): string | number | undefined =>
  staff.find(
    ({ first_name, id, last_name, staff_role }) =>
      staff_role === role && (id === value || value === `${first_name} ${last_name}`)
  )?.id;

const sortIds = (ids: string[]): string[] =>
  [...ids].sort((first, second) => first.localeCompare(second));

const normalizeCount = (count: number | null | undefined): number | null =>
  count === null || count === undefined ? null : Number(count);

const normalizeStaffValue = (
  staff: Staff[],
  value: string | number | undefined,
  role: 'Support' | 'Sales'
): string => `${getStaffId(staff, value, role) ?? value ?? ''}`;

const formatComparableDate = (date: Date | null): string =>
  date && isValid(date) ? format(date, DATE_FORMAT) : '';

const areAddonPayloadsEqual = (
  first: AccountAddonPayload[],
  second: AccountAddonPayload[]
): boolean =>
  JSON.stringify(
    first
      .map(({ count, id }) => ({ count: Number(count), id }))
      .sort((a, b) => a.id.localeCompare(b.id))
  ) ===
  JSON.stringify(
    second
      .map(({ count, id }) => ({ count: Number(count), id }))
      .sort((a, b) => a.id.localeCompare(b.id))
  );

const getPlanLogo = (plan: PricingPlan): string => PLAN_LOGOS[plan.code] || ProfessionalPlanLogo;

const parseAccountDate = (value?: string): Date | null => {
  const parseExactDate = (dateValue: string, dateFormat: string): Date | null => {
    const parsedDate = parse(dateValue, dateFormat, new Date(0));

    return isValid(parsedDate) && format(parsedDate, dateFormat) === dateValue ? parsedDate : null;
  };
  const calendarDate = value?.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  const slashDate = value?.match(/^\d{2}\/\d{2}\/\d{4}$/)?.[0];
  const date = calendarDate
    ? parseExactDate(calendarDate, DATE_FORMAT)
    : slashDate
    ? parseExactDate(slashDate, 'MM/dd/yyyy')
    : null;

  return date;
};

const disableBeforeToday = (date: Date): boolean =>
  isBefore(startOfDay(date), startOfDay(new Date()));

const disableSameOrBefore = (date: Date, referenceDate: Date): boolean =>
  isBefore(startOfDay(date), addDays(startOfDay(referenceDate), 1));

const getToday = (): Date => startOfDay(new Date());

const formatAccountDateForApi = (date: Date): string =>
  `${format(
    set(date, { hours: 0, milliseconds: 0, minutes: 0, seconds: 0 }),
    "yyyy-MM-dd'T'HH:mm:ss.SSS"
  )}Z`;

const getTitle = ({
  managePlan,
  editAccount,
}: Pick<AccountManagementModalProps, 'managePlan' | 'editAccount'>): string => {
  if (managePlan) {
    return ACCOUNT_MANAGEMENT_MODAL_COPY.managePlanTitle;
  }

  return editAccount
    ? ACCOUNT_MANAGEMENT_MODAL_COPY.editTitle
    : ACCOUNT_MANAGEMENT_MODAL_COPY.createTitle;
};

const getActionLabel = ({
  managePlan,
  editAccount,
}: Pick<AccountManagementModalProps, 'managePlan' | 'editAccount'>): string => {
  if (managePlan) {
    return ACCOUNT_MANAGEMENT_MODAL_COPY.managePlanAction;
  }

  return editAccount
    ? ACCOUNT_MANAGEMENT_MODAL_COPY.editAction
    : ACCOUNT_MANAGEMENT_MODAL_COPY.createAction;
};

const AccountManagementModal = ({
  managePlan,
  editAccount,
  onClose,
  account,
  isVisible,
  onAccountEdition,
}: AccountManagementModalProps): JSX.Element => {
  const history = useHistory();
  const voucherAssignmentV1 = useFlags()?.voucherAssignmentV1 ?? false;
  const { pricingPlans } = useContext(SessionContext) || {};
  const currentPlan = useMemo(
    () => resolveAccountPricingPlan(account, pricingPlans),
    [account, pricingPlans]
  );
  const visiblePlans = useMemo(
    () => (pricingPlans || []).filter((plan) => !plan.legacy || plan.id === currentPlan?.id),
    [currentPlan?.id, pricingPlans]
  );
  const isExistingAccountMode = editAccount || managePlan;
  const isCreateMode = !isExistingAccountMode;
  const isExpired = isExistingAccountMode && Boolean(account?.expire_date);
  const [accountName, setAccountName] = useState(account?.name || '');
  const [focusedSection, setFocusedSection] = useState<'accountName' | 'plan' | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState(currentPlan?.id || '');
  const [planLicenseCount, setPlanLicenseCount] = useState<number | null>(
    isExistingAccountMode ? (account?.licenses_details ? account.licenses_details.count : 0) : 0
  );
  const [planLicenseInputValue, setPlanLicenseInputValue] = useState(() =>
    isExistingAccountMode && account?.licenses_details?.count === null
      ? ''
      : `${isExistingAccountMode ? account?.licenses_details?.count ?? 0 : 0}`
  );
  const [isFreeTrial, setIsFreeTrial] = useState(account?.is_free_trial || false);
  const [isInviteOnly, setIsInviteOnly] = useState(account?.is_invite_only || false);
  const [startDate, setStartDate] = useState<Date | null>(() =>
    isCreateMode
      ? getToday()
      : parseAccountDate(account?.start_date) || parseAccountDate(account?.created_at)
  );
  const [endDate, setEndDate] = useState<Date | null>(() => parseAccountDate(account?.end_date));
  const [staff, setStaff] = useState<Staff[]>([]);
  const [support, setSupport] = useState<string | number | undefined>(
    account?.representative?.support
  );
  const [sales, setSales] = useState<string | number | undefined>(account?.representative?.sales);
  const [selectedAddonIds, setSelectedAddonIds] = useState<string[]>(
    () => account?.account_addons?.addons.map((addon) => addon.id) || []
  );
  const [addonCounts, setAddonCounts] = useState<Record<string, number>>(() =>
    (account?.account_addons?.addons || []).reduce(
      (counts, addon) => ({ ...counts, [addon.id]: addon.count }),
      {} as Record<string, number>
    )
  );
  const [addonInputValues, setAddonInputValues] = useState<Record<string, string>>({});
  const [certificationOptions, setCertificationOptions] = useState<CertificationOptionData[]>([]);
  const [selectedCertificationIds, setSelectedCertificationIds] = useState<string[]>([]);
  const [isCertificationOptionsOpen, setIsCertificationOptionsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPlanChangeConfirmationOpen, setIsPlanChangeConfirmationOpen] = useState(false);
  const [hasExistingCertificationAccess, setHasExistingCertificationAccess] = useState(false);
  const [displayedAddons, setDisplayedAddons] = useState<ModalAddon[]>([]);
  const [addonTransitionStates, setAddonTransitionStates] = useState<
    Record<string, AddonTransitionState>
  >({});
  const [loadedExistingAddonDataKey, setLoadedExistingAddonDataKey] = useState<string | null>(null);
  const shouldSelectAllCertificationOptions = useRef(false);
  const initialVoucherBalanceRef = useRef(0);
  const initialCertificationIdsRef = useRef<string[]>([]);
  const certificationOptionsRef = useRef<HTMLDivElement | null>(null);
  const certificationOptionsTriggerRef = useRef<HTMLButtonElement | null>(null);
  const modalShellRef = useRef<HTMLDivElement | null>(null);
  const submissionLockRef = useRef(false);
  const previousAvailableAddonsRef = useRef<ModalAddon[] | null>(null);
  const addonEntryTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const addonRemovalTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const shouldLoadExistingAddonData = isVisible && isExistingAccountMode;
  const existingAddonDataKey = account?.id || '';
  const selectedPlan = useMemo(
    () => (pricingPlans || []).find((plan) => plan.id === selectedPlanId),
    [pricingPlans, selectedPlanId]
  );
  const isCurrentPlanSelected = currentPlan
    ? `${selectedPlanId}` === `${currentPlan.id}`
    : !selectedPlanId;
  const availableAddons = useMemo(() => {
    const planAddons = getPlanAddons(selectedPlan, voucherAssignmentV1);
    const existingAddons =
      isExistingAccountMode && isCurrentPlanSelected
        ? [
            ...getExistingAddons(account),
            ...(voucherAssignmentV1 && hasExistingCertificationAccess
              ? [INE_CERTIFICATIONS_ADDON]
              : []),
          ]
        : [];

    return mergeAddons(planAddons, existingAddons);
  }, [
    account,
    hasExistingCertificationAccess,
    isExistingAccountMode,
    isCurrentPlanSelected,
    selectedPlan,
    voucherAssignmentV1,
  ]);
  const availableAddonIds = availableAddons.map((addon) => addon.id).join('|');
  const title = getTitle({ managePlan, editAccount });
  const actionLabel = getActionLabel({ managePlan, editAccount });

  useLayoutEffect(() => {
    // rc-dialog 8.6 focuses these sentinels, so they cannot also remain aria-hidden.
    const dialog = modalShellRef.current?.closest('.ant-modal');

    dialog
      ?.querySelectorAll(':scope > [tabindex="0"][aria-hidden="true"]')
      .forEach((sentinel) => sentinel.removeAttribute('aria-hidden'));
  }, [isVisible]);

  useEffect(() => {
    const nextPlanId = currentPlan?.id || '';

    setAccountName(account?.name || '');
    setFocusedSection(null);
    setSelectedPlanId(nextPlanId);
    setPlanLicenseCount(
      isExistingAccountMode ? (account?.licenses_details ? account.licenses_details.count : 0) : 0
    );
    setPlanLicenseInputValue(
      isExistingAccountMode && account?.licenses_details?.count === null
        ? ''
        : `${isExistingAccountMode ? account?.licenses_details?.count ?? 0 : 0}`
    );
    setIsFreeTrial(account?.is_free_trial || false);
    setIsInviteOnly(account?.is_invite_only || false);
    setStartDate(
      isCreateMode
        ? getToday()
        : parseAccountDate(account?.start_date) || parseAccountDate(account?.created_at)
    );
    setEndDate(parseAccountDate(account?.end_date));
    setSupport(account?.representative?.support);
    setSales(account?.representative?.sales);
    setSelectedAddonIds(account?.account_addons?.addons.map((addon) => addon.id) || []);
    setAddonCounts(
      (account?.account_addons?.addons || []).reduce(
        (counts, addon) => ({ ...counts, [addon.id]: addon.count }),
        {} as Record<string, number>
      )
    );
    setAddonInputValues({});
    setSelectedCertificationIds([]);
    setIsCertificationOptionsOpen(false);
    setHasExistingCertificationAccess(false);
    shouldSelectAllCertificationOptions.current = false;
    initialVoucherBalanceRef.current = 0;
    initialCertificationIdsRef.current = [];
  }, [account?.id, currentPlan?.id, editAccount, isCreateMode, isVisible, managePlan]);

  useEffect(() => {
    let isMounted = true;

    const loadStaff = async () => {
      const staffMembers = await getStaff();

      if (isMounted && !(staffMembers instanceof Error)) {
        setStaff(staffMembers);
      }
    };

    loadStaff();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return undefined;

    if (!voucherAssignmentV1) {
      setCertificationOptions([]);
      setSelectedCertificationIds([]);
      setHasExistingCertificationAccess(false);
      initialVoucherBalanceRef.current = 0;
      initialCertificationIdsRef.current = [];

      if (shouldLoadExistingAddonData) {
        setLoadedExistingAddonDataKey(existingAddonDataKey);
      }

      return undefined;
    }

    let isMounted = true;

    const loadCertificationOptions = async () => {
      try {
        const [certificationsResponse, voucherResponse] = await Promise.all([
          loadActiveCertifications(),
          isExistingAccountMode
            ? fetchVoucherSummary({ account_id: account?.id || '' })
            : Promise.resolve(undefined),
        ]);

        if (!isMounted) return;

        const activeCertifications = certificationsResponse.data.results.map((certification) => ({
          id: certification.id,
          code: certification.code,
          name: certification.name,
        }));
        const enabledCertificationIds =
          voucherResponse?.data.exams
            .filter((exam) => exam.is_enabled)
            .map((exam) => exam.certification_version_id) || [];
        const hasExistingCertifications =
          Boolean(voucherResponse?.data.voucher_balance.total_purchased) ||
          enabledCertificationIds.length > 0;
        const voucherBalance = voucherResponse?.data.voucher_balance.total_purchased ?? 0;

        setCertificationOptions(activeCertifications);

        if (isCreateMode && shouldSelectAllCertificationOptions.current) {
          setSelectedCertificationIds(
            activeCertifications.map((certification) => certification.id)
          );
          shouldSelectAllCertificationOptions.current = false;
        }

        if (isExistingAccountMode) {
          initialVoucherBalanceRef.current = voucherBalance;
          initialCertificationIdsRef.current = enabledCertificationIds;
          setHasExistingCertificationAccess(hasExistingCertifications);
          setSelectedCertificationIds(enabledCertificationIds);
          setAddonCounts((counts) => ({
            ...counts,
            [INE_CERTIFICATIONS_ADDON.id]: voucherBalance,
          }));

          if (hasExistingCertifications) {
            setSelectedAddonIds((addonIds) =>
              addonIds.includes(INE_CERTIFICATIONS_ADDON.id)
                ? addonIds
                : [...addonIds, INE_CERTIFICATIONS_ADDON.id]
            );
          }
        }
      } catch (error) {
        if (isMounted) {
          setCertificationOptions([]);
          if (isExistingAccountMode) {
            Notification({
              type: 'error',
              text:
                getErrorDetail(error) || ACCOUNT_MANAGEMENT_MODAL_COPY.loadAccountAddonDataError,
            });
            onClose('error');
          }
        }
      } finally {
        if (isMounted && shouldLoadExistingAddonData) {
          setLoadedExistingAddonDataKey(existingAddonDataKey);
        }
      }
    };

    loadCertificationOptions();

    return () => {
      isMounted = false;
    };
  }, [
    account?.id,
    existingAddonDataKey,
    isVisible,
    isCreateMode,
    isExistingAccountMode,
    shouldLoadExistingAddonData,
    voucherAssignmentV1,
  ]);

  useLayoutEffect(() => {
    const previousAvailableAddons = previousAvailableAddonsRef.current;

    if (!previousAvailableAddons) {
      setDisplayedAddons(availableAddons);
      setAddonTransitionStates(
        availableAddons.reduce<Record<string, AddonTransitionState>>(
          (states, addon) => ({ ...states, [addon.id]: 'visible' }),
          {}
        )
      );
      previousAvailableAddonsRef.current = availableAddons;
      return;
    }

    const nextAddonIds = new Set(availableAddons.map((addon) => addon.id));
    const previousAddonIds = new Set(previousAvailableAddons.map((addon) => addon.id));
    const removedAddonIds = [...previousAddonIds].filter((id) => !nextAddonIds.has(id));
    const addedAddonIds = [...nextAddonIds].filter((id) => !previousAddonIds.has(id));
    const nextAddonsById = new Map(availableAddons.map((addon) => [addon.id, addon]));

    addedAddonIds.forEach((id) => {
      const timer = addonRemovalTimersRef.current[id];

      if (timer) {
        clearTimeout(timer);
        delete addonRemovalTimersRef.current[id];
      }
    });

    removedAddonIds.forEach((id) => {
      const timer = addonEntryTimersRef.current[id];

      if (timer) {
        clearTimeout(timer);
        delete addonEntryTimersRef.current[id];
      }
    });

    setDisplayedAddons((currentAddons) => {
      if (!removedAddonIds.length) {
        return availableAddons;
      }

      const currentAddonIds = new Set(currentAddons.map((addon) => addon.id));
      const addedAddons = availableAddons.filter((addon) => !currentAddonIds.has(addon.id));

      return [
        ...currentAddons.map((addon) => nextAddonsById.get(addon.id) || addon),
        ...addedAddons,
      ];
    });
    setAddonTransitionStates((currentStates) => {
      const nextStates: Record<string, AddonTransitionState> = {};

      availableAddons.forEach((addon) => {
        nextStates[addon.id] = addedAddonIds.includes(addon.id)
          ? 'entering'
          : currentStates[addon.id] === 'leaving'
          ? 'entering'
          : currentStates[addon.id] || 'visible';
      });
      removedAddonIds.forEach((id) => {
        nextStates[id] = 'leaving';
      });

      return nextStates;
    });

    addedAddonIds.forEach((id) => {
      const existingTimer = addonEntryTimersRef.current[id];

      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      addonEntryTimersRef.current[id] = setTimeout(() => {
        setAddonTransitionStates((currentStates) =>
          currentStates[id] === 'entering' ? { ...currentStates, [id]: 'visible' } : currentStates
        );
        delete addonEntryTimersRef.current[id];
      }, ADDON_TRANSITION_DURATION);
    });

    removedAddonIds.forEach((id) => {
      const existingTimer = addonRemovalTimersRef.current[id];

      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      addonRemovalTimersRef.current[id] = setTimeout(() => {
        setDisplayedAddons((currentAddons) => currentAddons.filter((addon) => addon.id !== id));
        setAddonTransitionStates((currentStates) => {
          const remainingStates = { ...currentStates };

          delete remainingStates[id];

          return remainingStates;
        });
        delete addonRemovalTimersRef.current[id];
      }, ADDON_TRANSITION_DURATION);
    });

    previousAvailableAddonsRef.current = availableAddons;
  }, [availableAddonIds, availableAddons]);

  useEffect(
    () => () => {
      Object.values(addonEntryTimersRef.current).forEach((timer) => clearTimeout(timer));
      Object.values(addonRemovalTimersRef.current).forEach((timer) => clearTimeout(timer));
    },
    []
  );

  useEffect(() => {
    if (!availableAddons.some((addon) => addon.id === INE_CERTIFICATIONS_ADDON.id)) {
      setIsCertificationOptionsOpen(false);
    }
  }, [availableAddonIds, availableAddons]);

  useEffect(() => {
    if (!isCertificationOptionsOpen) return undefined;

    const closeCertificationOptions = (event: MouseEvent | TouchEvent) => {
      const target = event.target;

      if (
        target instanceof Node &&
        !certificationOptionsRef.current?.contains(target) &&
        !certificationOptionsTriggerRef.current?.contains(target)
      ) {
        setIsCertificationOptionsOpen(false);
      }
    };

    document.addEventListener('mousedown', closeCertificationOptions);
    document.addEventListener('touchstart', closeCertificationOptions);

    return () => {
      document.removeEventListener('mousedown', closeCertificationOptions);
      document.removeEventListener('touchstart', closeCertificationOptions);
    };
  }, [isCertificationOptionsOpen]);

  const getManualCount = (value: string): number | null => {
    if (!/^\d+$/.test(value)) return null;

    const count = Number(value);

    return Number.isSafeInteger(count) ? count : null;
  };

  const notifyInvalidQuantity = (
    description = ACCOUNT_MANAGEMENT_MODAL_COPY.invalidQuantityError
  ) => {
    Notification({
      type: 'error',
      text: ACCOUNT_MANAGEMENT_MODAL_COPY.validationErrorTitle,
      description,
    });
  };

  const minimumPlanLicenseCount = isExistingAccountMode ? account?.licenses_details?.used ?? 0 : 0;

  const getDefaultAddonCount = (addonId: string) =>
    addonId === INE_CERTIFICATIONS_ADDON.id
      ? 0
      : planLicenseCount ?? account?.licenses_details?.count ?? 1;

  const getAddonCount = (addonId: string): number =>
    addonCounts[addonId] ?? getDefaultAddonCount(addonId);

  const getAddonMaximum = (addonId: string): number =>
    addonId === INE_CERTIFICATIONS_ADDON.id
      ? Number.MAX_SAFE_INTEGER
      : planLicenseCount ?? Number.MAX_SAFE_INTEGER;

  const toggleAddon = (addonId: string) => {
    const isSelected = selectedAddonIds.includes(addonId);

    setSelectedAddonIds((selectedAddons) =>
      selectedAddons.includes(addonId)
        ? selectedAddons.filter((id) => id !== addonId)
        : [...selectedAddons, addonId]
    );
    setAddonCounts((counts) => ({
      ...counts,
      [addonId]: counts[addonId] ?? getDefaultAddonCount(addonId),
    }));

    if (addonId === INE_CERTIFICATIONS_ADDON.id) {
      shouldSelectAllCertificationOptions.current = !isSelected;
      setIsCertificationOptionsOpen(false);

      if (isSelected) {
        setAddonCounts((counts) => ({
          ...counts,
          [addonId]: 0,
        }));
        setAddonInputValues((values) => ({
          ...values,
          [addonId]: '0',
        }));
        setSelectedCertificationIds([]);
      } else {
        const restoredVoucherBalance = isExistingAccountMode
          ? initialVoucherBalanceRef.current
          : getAddonCount(addonId);
        const restoredCertificationIds =
          isExistingAccountMode && initialCertificationIdsRef.current.length > 0
            ? initialCertificationIdsRef.current
            : certificationOptions.map((certification) => certification.id);

        setAddonCounts((counts) => ({
          ...counts,
          [addonId]: restoredVoucherBalance,
        }));
        setAddonInputValues((values) => ({
          ...values,
          [addonId]: `${restoredVoucherBalance}`,
        }));
        setSelectedCertificationIds(restoredCertificationIds);
      }
    }
  };

  const updateAddonCount = (addonId: string, amount: number) => {
    const maximum = getAddonMaximum(addonId);
    const clampCount = (count: number) => Math.min(maximum, Math.max(0, count + amount));

    setAddonCounts((counts) => ({
      ...counts,
      [addonId]: clampCount(Number(counts[addonId] ?? getDefaultAddonCount(addonId))),
    }));
    setAddonInputValues((values) => {
      const currentInputCount = getManualCount(values[addonId] ?? `${getAddonCount(addonId)}`);

      return {
        ...values,
        [addonId]: `${clampCount(currentInputCount ?? getAddonCount(addonId))}`,
      };
    });
  };

  const updateAddonCountFromInput = (addonId: string, value: string) => {
    setAddonInputValues((values) => ({ ...values, [addonId]: value }));
    const count = getManualCount(value);

    if (count !== null && count <= getAddonMaximum(addonId)) {
      setAddonCounts((counts) => ({ ...counts, [addonId]: count }));
    }
  };

  const normalizeAddonCountInput = (addonId: string) => {
    const currentCount = getAddonCount(addonId);
    const count = getManualCount(addonInputValues[addonId] ?? `${currentCount}`);

    if (count === null) {
      setAddonInputValues((values) => ({ ...values, [addonId]: `${currentCount}` }));
      notifyInvalidQuantity();
      return;
    }

    const maximum = getAddonMaximum(addonId);
    const normalizedCount = Math.min(count, maximum);

    setAddonCounts((counts) => ({ ...counts, [addonId]: normalizedCount }));
    setAddonInputValues((values) => ({ ...values, [addonId]: `${normalizedCount}` }));

    if (count > maximum) {
      notifyInvalidQuantity(ACCOUNT_MANAGEMENT_MODAL_COPY.addonCountError);
    }
  };

  const updatePlanLicenseCount = (amount: number) => {
    const clampCount = (count: number) =>
      Math.min(MAX_LICENSES_ACCOUNT, Math.max(minimumPlanLicenseCount, count + amount));

    setPlanLicenseCount((count) => clampCount(Number(count ?? minimumPlanLicenseCount)));
    setPlanLicenseInputValue(
      (value) => `${clampCount(getManualCount(value) ?? minimumPlanLicenseCount)}`
    );
  };

  const updatePlanLicenseCountFromInput = (value: string) => {
    setPlanLicenseInputValue(value);
    const count = getManualCount(value);

    if (count !== null && count >= minimumPlanLicenseCount && count <= MAX_LICENSES_ACCOUNT) {
      setPlanLicenseCount(count);
    }
  };

  const normalizePlanLicenseInput = () => {
    if (planLicenseCount === null) return;

    const count = getManualCount(planLicenseInputValue);

    if (count === null) {
      setPlanLicenseInputValue(`${planLicenseCount}`);
      notifyInvalidQuantity();
      return;
    }

    const normalizedCount = Math.min(
      MAX_LICENSES_ACCOUNT,
      Math.max(minimumPlanLicenseCount, count)
    );

    setPlanLicenseCount(normalizedCount);
    setPlanLicenseInputValue(`${normalizedCount}`);

    if (normalizedCount !== count) {
      notifyInvalidQuantity();
    }
  };

  const toggleUnlimitedLicenses = () => {
    if (planLicenseCount === null) {
      setPlanLicenseCount(minimumPlanLicenseCount);
      setPlanLicenseInputValue(`${minimumPlanLicenseCount}`);
    } else {
      setPlanLicenseCount(null);
    }
  };

  const toggleFreeTrial = () => {
    const nextIsFreeTrial = !isFreeTrial;

    setIsFreeTrial(nextIsFreeTrial);

    if (nextIsFreeTrial && !endDate) {
      const defaultStartDate = startDate || getToday();

      if (!startDate) {
        setStartDate(defaultStartDate);
      }
      setEndDate(addDays(defaultStartDate, 14));
    }
  };

  const updateStartDate = (date: Date | null) => {
    setStartDate(date);

    if (date && endDate && disableSameOrBefore(endDate, date)) {
      setEndDate(null);
    }
  };

  const toggleCertification = (certificationId: string) => {
    setSelectedCertificationIds((selectedCertifications) =>
      selectedCertifications.includes(certificationId)
        ? selectedCertifications.filter((id) => id !== certificationId)
        : [...selectedCertifications, certificationId]
    );
  };

  const toggleAllCertifications = () => {
    setSelectedCertificationIds((selectedCertifications) =>
      selectedCertifications.length === certificationOptions.length
        ? []
        : certificationOptions.map((certification) => certification.id)
    );
  };

  const getValidatedQuantities = ():
    | {
        addons: AccountAddonPayload[];
        enabledCertificationIds: string[];
        licenseCount: number | null;
        voucherBalance: number;
      }
    | undefined => {
    let normalizedPlanLicenseCount: number | null = null;

    if (planLicenseCount !== null) {
      const count = getManualCount(planLicenseInputValue);

      if (count === null || count < minimumPlanLicenseCount || count > MAX_LICENSES_ACCOUNT) {
        notifyInvalidQuantity();
        return undefined;
      }
      normalizedPlanLicenseCount = count;
    }

    const availableAddonIdSet = new Set(availableAddons.map((addon) => addon.id));
    const selectedAvailableAddonIds = selectedAddonIds.filter((addonId) =>
      availableAddonIdSet.has(addonId)
    );
    const normalizedAddonCounts: Record<string, number> = {};

    for (const addonId of selectedAvailableAddonIds) {
      const count = getManualCount(
        addonInputValues[addonId] ?? `${addonCounts[addonId] ?? getDefaultAddonCount(addonId)}`
      );

      if (count === null) {
        notifyInvalidQuantity();
        return undefined;
      }
      if (
        addonId !== INE_CERTIFICATIONS_ADDON.id &&
        normalizedPlanLicenseCount !== null &&
        count > normalizedPlanLicenseCount
      ) {
        notifyInvalidQuantity(ACCOUNT_MANAGEMENT_MODAL_COPY.addonCountError);
        return undefined;
      }

      normalizedAddonCounts[addonId] = count;
    }

    if (planLicenseCount !== null) {
      setPlanLicenseCount(normalizedPlanLicenseCount);
      setPlanLicenseInputValue(`${normalizedPlanLicenseCount}`);
    }
    setAddonCounts((counts) => ({ ...counts, ...normalizedAddonCounts }));
    setAddonInputValues((values) => ({
      ...values,
      ...Object.fromEntries(
        Object.entries(normalizedAddonCounts).map(([addonId, count]) => [addonId, `${count}`])
      ),
    }));

    const certificationsSelected = selectedAvailableAddonIds.includes(INE_CERTIFICATIONS_ADDON.id);

    return {
      addons: selectedAvailableAddonIds
        .filter((addonId) => addonId !== INE_CERTIFICATIONS_ADDON.id)
        .map((addonId) => ({ id: addonId, count: normalizedAddonCounts[addonId] })),
      enabledCertificationIds: certificationsSelected ? selectedCertificationIds : [],
      licenseCount: normalizedPlanLicenseCount,
      voucherBalance: certificationsSelected
        ? normalizedAddonCounts[INE_CERTIFICATIONS_ADDON.id]
        : 0,
    };
  };

  const getPlanChangeSet = ({
    addons,
    enabledCertificationIds,
    licenseCount,
    voucherBalance,
  }: {
    addons: AccountAddonPayload[];
    enabledCertificationIds: string[];
    licenseCount: number | null;
    voucherBalance: number;
  }) => {
    const initialAddons = (account?.account_addons?.addons || []).map(({ count, id }) => ({
      count: Number(count),
      id,
    }));
    const planChanged = `${selectedPlanId}` !== `${currentPlan?.id ?? ''}`;
    const licenseChanged =
      normalizeCount(licenseCount) !== normalizeCount(account?.licenses_details?.count);
    const addonsChanged = !areAddonPayloadsEqual(addons, initialAddons);
    const voucherBalanceChanged =
      voucherAssignmentV1 && Number(voucherBalance) !== Number(initialVoucherBalanceRef.current);
    const certificationsChanged =
      voucherAssignmentV1 &&
      JSON.stringify(sortIds(enabledCertificationIds)) !==
        JSON.stringify(sortIds(initialCertificationIdsRef.current));

    return {
      addonsChanged,
      certificationsChanged,
      licenseChanged,
      planChanged,
      shouldChangePlan: planChanged || licenseChanged || addonsChanged,
      voucherBalanceChanged,
    };
  };

  const hasAccountDetailsChanged = (): boolean => {
    const initialStartDate =
      parseAccountDate(account?.start_date) || parseAccountDate(account?.created_at);
    const initialEndDate = parseAccountDate(account?.end_date);

    return (
      accountName.trim() !== (account?.name || '').trim() ||
      formatComparableDate(startDate) !== formatComparableDate(initialStartDate) ||
      formatComparableDate(endDate) !== formatComparableDate(initialEndDate) ||
      isFreeTrial !== Boolean(account?.is_free_trial) ||
      isInviteOnly !== Boolean(account?.is_invite_only) ||
      normalizeStaffValue(staff, support, 'Support') !==
        normalizeStaffValue(staff, account?.representative?.support, 'Support') ||
      normalizeStaffValue(staff, sales, 'Sales') !==
        normalizeStaffValue(staff, account?.representative?.sales, 'Sales')
    );
  };

  const handleAccountSubmit = async (planChangesConfirmed = false) => {
    if (submissionLockRef.current) return;

    const validatedQuantities = getValidatedQuantities();

    if (!validatedQuantities) return;

    const { addons, enabledCertificationIds, licenseCount, voucherBalance } = validatedQuantities;
    const supportId = getStaffId(staff, support, 'Support');
    const salesId = getStaffId(staff, sales, 'Sales');
    if (
      !accountName.trim() ||
      !startDate ||
      !endDate ||
      !selectedPlanId ||
      (isCreateMode && (!supportId || !salesId))
    ) {
      Notification({
        type: 'error',
        text: ACCOUNT_MANAGEMENT_MODAL_COPY.validationErrorTitle,
        description: ACCOUNT_MANAGEMENT_MODAL_COPY.requiredFieldsError,
      });
      return;
    }

    if (
      isCreateMode &&
      voucherAssignmentV1 &&
      voucherBalance > 0 &&
      certificationOptions.length > 0 &&
      enabledCertificationIds.length === 0
    ) {
      Notification({
        type: 'error',
        text: ACCOUNT_MANAGEMENT_MODAL_COPY.validationErrorTitle,
        description: ACCOUNT_MANAGEMENT_MODAL_COPY.certificationSelectionError,
      });
      return;
    }

    const accountData = {
      name: accountName.trim(),
      start_date: formatAccountDateForApi(startDate),
      end_date: formatAccountDateForApi(endDate),
      license_count: licenseCount,
      is_free_trial: isFreeTrial,
      is_invite_only: isInviteOnly,
      support: supportId,
      sales: salesId,
      addons,
    };

    const accountDetailsChanged = editAccount && !isExpired && hasAccountDetailsChanged();
    const planChangeSet = getPlanChangeSet(validatedQuantities);
    const hasPlanUpdates =
      planChangeSet.shouldChangePlan ||
      planChangeSet.voucherBalanceChanged ||
      planChangeSet.certificationsChanged;

    if (editAccount && !isExpired && !accountDetailsChanged && !hasPlanUpdates) {
      Notification({
        type: 'info',
        text: ACCOUNT_MANAGEMENT_MODAL_COPY.noPlanChangesTitle,
        description: ACCOUNT_MANAGEMENT_MODAL_COPY.noAccountChangesDescription,
      });
      return;
    }

    if (editAccount && !isExpired && hasPlanUpdates && !planChangesConfirmed) {
      setIsPlanChangeConfirmationOpen(true);
      return;
    }

    submissionLockRef.current = true;
    setIsSubmitting(true);
    try {
      if (editAccount) {
        if (isExpired || accountDetailsChanged) {
          const response = isExpired
            ? await reactivateAccount(account?.id || '', {
                start_date: accountData.start_date,
                end_date: accountData.end_date,
              })
            : await editAccountService(
                {
                  name: accountData.name,
                  start_date: accountData.start_date,
                  end_date: accountData.end_date,
                  is_free_trial: accountData.is_free_trial,
                  is_invite_only: accountData.is_invite_only,
                  support: accountData.support,
                  sales: accountData.sales,
                },
                account?.id || ''
              );

          if (!hasPlanUpdates) {
            Notification({
              type: 'success',
              text: `${response.data?.name || accountData.name} ${
                isExpired
                  ? ACCOUNT_MANAGEMENT_MODAL_COPY.accountReactivatedSuffix
                  : ACCOUNT_MANAGEMENT_MODAL_COPY.accountUpdatedSuffix
              }`,
              duration: 8,
            });
          }
        }

        if (!isExpired && hasPlanUpdates) {
          const planUpdateSucceeded = await handleChangePlan({
            closeOnSuccess: false,
            notifyWhenUnchanged: false,
            submissionAlreadyStarted: true,
          });

          if (!planUpdateSucceeded) return;
        }
      } else {
        const response = await createAccount({
          ...accountData,
          pricing_plan: selectedPlanId,
          ...(voucherAssignmentV1
            ? {
                initial_voucher_balance: { quantity: voucherBalance },
                enabled_certification_version_ids: enabledCertificationIds,
              }
            : {}),
        });

        Notification({
          type: 'success',
          text: (
            <span>
              {`${response.data?.name || accountData.name} ${
                ACCOUNT_MANAGEMENT_MODAL_COPY.accountCreatedSuffix
              } `}
              <span
                className="notification-link"
                onClick={() => history.push(`/account/${response.data?.id}`)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    history.push(`/account/${response.data?.id}`);
                  }
                }}
                role="link"
                tabIndex={0}
              >
                {ACCOUNT_MANAGEMENT_MODAL_COPY.goToAccountLabel}
              </span>
            </span>
          ),
          duration: 8,
        });
      }

      onClose('ok');
    } catch (error) {
      Notification({
        type: 'error',
        text:
          getErrorDetail(error) ||
          (editAccount
            ? ACCOUNT_MANAGEMENT_MODAL_COPY.editError
            : ACCOUNT_MANAGEMENT_MODAL_COPY.createError),
      });
      onClose('error');
    } finally {
      submissionLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleChangePlan = async ({
    closeOnSuccess = true,
    notifyWhenUnchanged = true,
    submissionAlreadyStarted = false,
  }: {
    closeOnSuccess?: boolean;
    notifyWhenUnchanged?: boolean;
    submissionAlreadyStarted?: boolean;
  } = {}): Promise<boolean> => {
    if (!submissionAlreadyStarted) {
      if (submissionLockRef.current) return false;
      submissionLockRef.current = true;
    }

    const releaseSubmissionLock = () => {
      if (!submissionAlreadyStarted) submissionLockRef.current = false;
    };

    if (!account?.id || !selectedPlanId) {
      releaseSubmissionLock();
      return false;
    }

    const validatedQuantities = getValidatedQuantities();

    if (!validatedQuantities) {
      releaseSubmissionLock();
      return false;
    }

    const { addons, enabledCertificationIds, licenseCount, voucherBalance } = validatedQuantities;

    const { certificationsChanged, shouldChangePlan, voucherBalanceChanged } =
      getPlanChangeSet(validatedQuantities);

    if (!shouldChangePlan && !voucherBalanceChanged && !certificationsChanged) {
      if (notifyWhenUnchanged) {
        Notification({
          type: 'info',
          text: ACCOUNT_MANAGEMENT_MODAL_COPY.noPlanChangesTitle,
          description: ACCOUNT_MANAGEMENT_MODAL_COPY.noPlanChangesDescription,
        });
      }
      releaseSubmissionLock();
      return true;
    }

    const operations: { name: string; promise: Promise<unknown> }[] = [];

    if (shouldChangePlan) {
      operations.push({
        name: ACCOUNT_MANAGEMENT_MODAL_COPY.planOperationLabel,
        promise: changeAccountPlan(
          { pricing_plan: selectedPlanId, license_count: licenseCount, addons },
          account.id
        ),
      });
    }
    if (voucherBalanceChanged) {
      operations.push({
        name: ACCOUNT_MANAGEMENT_MODAL_COPY.voucherBalanceOperationLabel,
        promise: updateVoucherBalance(account.id, voucherBalance),
      });
    }
    if (certificationsChanged) {
      operations.push({
        name: ACCOUNT_MANAGEMENT_MODAL_COPY.enabledCertificationsOperationLabel,
        promise: updateEnabledExams(account.id, enabledCertificationIds),
      });
    }

    setIsSubmitting(true);
    try {
      const results = await Promise.allSettled(operations.map(({ promise }) => promise));
      const succeeded: string[] = [];
      const failed: string[] = [];
      let planResponse: { data: Account } | null = null;
      let planFailureDetail: string | undefined;

      results.forEach((result, index) => {
        const operationName = operations[index].name;

        if (result.status === 'fulfilled') {
          succeeded.push(operationName);
          if (operationName === ACCOUNT_MANAGEMENT_MODAL_COPY.planOperationLabel) {
            planResponse = result.value as { data: Account };
          }
        } else {
          failed.push(operationName);
          if (operationName === ACCOUNT_MANAGEMENT_MODAL_COPY.planOperationLabel) {
            planFailureDetail = getErrorDetail(result.reason);
          }
        }
      });

      if (planResponse && onAccountEdition) {
        onAccountEdition(planResponse.data);
      }

      if (succeeded.length) {
        Notification({
          type: 'success',
          text: `${account.name}: ${
            ACCOUNT_MANAGEMENT_MODAL_COPY.successfullyUpdatedLabel
          } ${succeeded.join(', ')}`,
          duration: 8,
        });
      }

      if (failed.length) {
        Notification({
          type: 'error',
          text: `${ACCOUNT_MANAGEMENT_MODAL_COPY.failedToUpdateLabel}: ${failed.join(', ')}`,
          description:
            planFailureDetail || ACCOUNT_MANAGEMENT_MODAL_COPY.planUpdateFailureDescription,
          duration: 8,
        });

        if (voucherAssignmentV1) {
          if (failed.includes(ACCOUNT_MANAGEMENT_MODAL_COPY.voucherBalanceOperationLabel)) {
            setAddonCounts((counts) => ({
              ...counts,
              [INE_CERTIFICATIONS_ADDON.id]: initialVoucherBalanceRef.current,
            }));
          }
          if (failed.includes(ACCOUNT_MANAGEMENT_MODAL_COPY.enabledCertificationsOperationLabel)) {
            setSelectedCertificationIds(initialCertificationIdsRef.current);
          }
        }

        return false;
      }

      if (closeOnSuccess) {
        onClose('ok');
      }
      return true;
    } catch (error) {
      if (voucherAssignmentV1) {
        setAddonCounts((counts) => ({
          ...counts,
          [INE_CERTIFICATIONS_ADDON.id]: initialVoucherBalanceRef.current,
        }));
        setSelectedCertificationIds(initialCertificationIdsRef.current);
      }

      Notification({
        type: 'error',
        text: ACCOUNT_MANAGEMENT_MODAL_COPY.changePlanError,
        description:
          getErrorDetail(error) || ACCOUNT_MANAGEMENT_MODAL_COPY.planUpdateFailureDescription,
      });
      return false;
    } finally {
      releaseSubmissionLock();
      setIsSubmitting(false);
    }
  };

  const allCertificationsSelected =
    certificationOptions.length > 0 &&
    selectedCertificationIds.length === certificationOptions.length;
  const certificationSelectionStatus = allCertificationsSelected
    ? ACCOUNT_MANAGEMENT_MODAL_COPY.allLabel
    : `${selectedCertificationIds.length} ${ACCOUNT_MANAGEMENT_MODAL_COPY.selectedLabel}`;

  if (shouldLoadExistingAddonData && loadedExistingAddonDataKey !== existingAddonDataKey) {
    return null;
  }

  return (
    <Modal
      centered
      closable={false}
      footer={null}
      fullscreen={isMobile}
      maskClosable={true}
      onCancel={() => onClose()}
      visible={isVisible}
      width={isMobile ? '100%' : '595px'}
    >
      <ModalShell data-testid="account-management-modal" ref={modalShellRef}>
        <Header data-testid="account-management-modal-header">
          <Title>{title}</Title>
          <CloseButton
            aria-label={ACCOUNT_MANAGEMENT_MODAL_COPY.closeLabel}
            autoFocus
            onClick={() => onClose()}
          >
            <Icon icon="Close" size={'default'} />
          </CloseButton>
        </Header>
        <Content data-testid="account-management-modal-content">
          <AccountNameField data-section="account-name">
            <AccountNameLabel htmlFor="account-management-account-name">
              {ACCOUNT_MANAGEMENT_MODAL_COPY.accountNameLabel}
            </AccountNameLabel>
            <AccountNameInput
              id="account-management-account-name"
              onChange={(event) => setAccountName(event.target.value)}
              onFocus={() => setFocusedSection('accountName')}
              readOnly={managePlan || isExpired}
              value={accountName}
            />
          </AccountNameField>
          <PlanOptions data-section="plan" onFocus={() => setFocusedSection('plan')}>
            <PlanLegend>{ACCOUNT_MANAGEMENT_MODAL_COPY.planLabel}</PlanLegend>
            {visiblePlans.map((plan) => {
              const isCurrentLegacyPlan = plan.legacy && plan.id === currentPlan?.id;
              const isPlanDisabled = isCurrentLegacyPlan || isExpired;
              const isSelected = selectedPlanId === plan.id;
              const isFocused = isSelected && focusedSection === 'plan';

              return (
                <PlanOption
                  data-focused={isFocused}
                  disabled={isPlanDisabled}
                  focused={isFocused}
                  key={plan.id}
                >
                  <PlanRadio
                    aria-label={ACCOUNT_MANAGEMENT_MODAL_COPY.planOptionLabel(
                      plan.name,
                      plan.legacy ? ACCOUNT_MANAGEMENT_MODAL_COPY.legacyPlanLabel : ''
                    )}
                    checked={isSelected}
                    disabled={isPlanDisabled}
                    focused={isFocused}
                    name="account-management-plan"
                    onChange={() => {
                      setSelectedPlanId(plan.id);
                      setFocusedSection('plan');
                    }}
                    type="radio"
                    value={plan.id}
                  />
                  <PlanIcon alt="" src={getPlanLogo(plan)} />
                  <PlanName>
                    <PlanTitle>{plan.name}</PlanTitle>
                    {plan.legacy && (
                      <LegacyBadge>{ACCOUNT_MANAGEMENT_MODAL_COPY.legacyPlanLabel}</LegacyBadge>
                    )}
                  </PlanName>
                  {isSelected && (
                    <PlanLicenseControls>
                      <AddonLabel>{ACCOUNT_MANAGEMENT_MODAL_COPY.licensesLabel}</AddonLabel>
                      <AddonCounter
                        aria-label={ACCOUNT_MANAGEMENT_MODAL_COPY.planQuantityLabel(plan.name)}
                      >
                        <AddonCounterValue
                          aria-label={ACCOUNT_MANAGEMENT_MODAL_COPY.planLicensesLabel}
                          data-testid="plan-license-count"
                          disabled={isExpired}
                          inputMode="numeric"
                          onChange={(event) => updatePlanLicenseCountFromInput(event.target.value)}
                          onBlur={normalizePlanLicenseInput}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') event.currentTarget.blur();
                          }}
                          readOnly={planLicenseCount === null}
                          value={planLicenseCount === null ? '∞' : planLicenseInputValue}
                        />
                        <AddonCounterButton
                          aria-label={ACCOUNT_MANAGEMENT_MODAL_COPY.increasePlanLicensesLabel}
                          disabled={
                            isExpired ||
                            planLicenseCount === null ||
                            planLicenseCount >= MAX_LICENSES_ACCOUNT
                          }
                          onClick={() => updatePlanLicenseCount(1)}
                          type="button"
                        >
                          <Icon icon="PlusNumeric" />
                        </AddonCounterButton>
                        <AddonCounterButton
                          aria-label={ACCOUNT_MANAGEMENT_MODAL_COPY.decreasePlanLicensesLabel}
                          disabled={
                            isExpired ||
                            planLicenseCount === null ||
                            planLicenseCount <= minimumPlanLicenseCount
                          }
                          onClick={() => updatePlanLicenseCount(-1)}
                          type="button"
                        >
                          <Icon icon="MinusNumeric" />
                        </AddonCounterButton>
                      </AddonCounter>
                      <PlanUnlimited>
                        <AddonCheckbox>
                          <Checkbox
                            aria-label={ACCOUNT_MANAGEMENT_MODAL_COPY.unlimitedLabel}
                            checked={planLicenseCount === null}
                            controlled
                            disabled={isExpired}
                            onChange={toggleUnlimitedLicenses}
                          />
                        </AddonCheckbox>
                        <UnlimitedLabel>
                          {ACCOUNT_MANAGEMENT_MODAL_COPY.unlimitedLabel}
                        </UnlimitedLabel>
                      </PlanUnlimited>
                    </PlanLicenseControls>
                  )}
                </PlanOption>
              );
            })}
          </PlanOptions>
          <Addons
            aria-hidden={!displayedAddons.length}
            aria-labelledby="account-management-addons-title"
            data-section="addons"
            $overlayOpen={isCertificationOptionsOpen}
            $visible={Boolean(displayedAddons.length)}
          >
            <AddonsTitle id="account-management-addons-title">
              {ACCOUNT_MANAGEMENT_MODAL_COPY.addonsLabel}
            </AddonsTitle>
            {displayedAddons.length > 0 && (
              <AddonList>
                {displayedAddons.map((addon, index) => {
                  const isSelected = selectedAddonIds.includes(addon.id);
                  const count = addonCounts[addon.id] ?? getDefaultAddonCount(addon.id);
                  const inputValue = addonInputValues[addon.id] ?? `${count}`;
                  const isCertifications = addon.id === INE_CERTIFICATIONS_ADDON.id;
                  const transitionState = addonTransitionStates[addon.id] || 'visible';
                  const isLeaving = transitionState === 'leaving';

                  return (
                    <AddonEntry
                      aria-hidden={isLeaving}
                      data-addon-id={addon.id}
                      data-transition-state={transitionState}
                      key={addon.id}
                      $transitionState={transitionState}
                    >
                      <AddonOption
                        $disabled={isExpired || isLeaving}
                        onClick={() => {
                          if (!isExpired && !isLeaving) toggleAddon(addon.id);
                        }}
                        separator={index > 0}
                      >
                        <AddonCheckbox onClick={(event) => event.stopPropagation()}>
                          <Checkbox
                            aria-label={addon.name}
                            checked={isSelected}
                            controlled
                            disabled={isExpired}
                            onChange={() => toggleAddon(addon.id)}
                            tabIndex={isLeaving ? -1 : 0}
                          />
                        </AddonCheckbox>
                        <AddonDetails>
                          <AddonName>
                            {addon.name}
                            {isCertifications && isSelected && (
                              <CertificationSelectionStatus data-testid="certification-selection-status">
                                {certificationSelectionStatus}
                              </CertificationSelectionStatus>
                            )}
                          </AddonName>
                          {isCertifications && isSelected && (
                            <AdvancedOptionsButton
                              aria-expanded={isCertificationOptionsOpen}
                              disabled={isExpired}
                              onClick={(event) => {
                                event.stopPropagation();
                                setIsCertificationOptionsOpen((isOpen) => !isOpen);
                              }}
                              ref={certificationOptionsTriggerRef}
                              tabIndex={isLeaving ? -1 : 0}
                              type="button"
                            >
                              {ACCOUNT_MANAGEMENT_MODAL_COPY.advancedOptionsLabel}
                              <Icon icon="ChevronDownPurple" size="smaller" />
                            </AdvancedOptionsButton>
                          )}
                        </AddonDetails>
                        {isSelected && (
                          <AddonControls onClick={(event) => event.stopPropagation()}>
                            <AddonLabel>{addon.quantityLabel}</AddonLabel>
                            <AddonCounter
                              aria-label={ACCOUNT_MANAGEMENT_MODAL_COPY.addonQuantityLabel(
                                addon.name,
                                addon.quantityLabel
                              )}
                            >
                              <AddonCounterValue
                                aria-label={ACCOUNT_MANAGEMENT_MODAL_COPY.addonQuantityLabel(
                                  addon.name,
                                  addon.quantityLabel
                                )}
                                data-testid={`addon-count-${addon.id}`}
                                disabled={isExpired}
                                inputMode="numeric"
                                onChange={(event) =>
                                  updateAddonCountFromInput(addon.id, event.target.value)
                                }
                                onBlur={() => normalizeAddonCountInput(addon.id)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') event.currentTarget.blur();
                                }}
                                tabIndex={!isLeaving ? 0 : -1}
                                value={inputValue}
                              />
                              <AddonCounterButton
                                aria-label={ACCOUNT_MANAGEMENT_MODAL_COPY.increaseAddonQuantityLabel(
                                  addon.name,
                                  addon.quantityLabel
                                )}
                                disabled={isExpired || count >= getAddonMaximum(addon.id)}
                                onClick={() => updateAddonCount(addon.id, 1)}
                                tabIndex={!isLeaving ? 0 : -1}
                                type="button"
                              >
                                <Icon icon="PlusNumeric" />
                              </AddonCounterButton>
                              <AddonCounterButton
                                aria-label={ACCOUNT_MANAGEMENT_MODAL_COPY.decreaseAddonQuantityLabel(
                                  addon.name,
                                  addon.quantityLabel
                                )}
                                disabled={isExpired || count === 0}
                                onClick={() => updateAddonCount(addon.id, -1)}
                                tabIndex={!isLeaving ? 0 : -1}
                                type="button"
                              >
                                <Icon icon="MinusNumeric" />
                              </AddonCounterButton>
                            </AddonCounter>
                          </AddonControls>
                        )}
                      </AddonOption>
                      {isCertifications && isSelected && (
                        <CertificationOptions
                          aria-hidden={!isCertificationOptionsOpen}
                          $visible={isCertificationOptionsOpen}
                          ref={certificationOptionsRef}
                        >
                          <CertificationOptionsTitle>
                            {ACCOUNT_MANAGEMENT_MODAL_COPY.vouchersAccessLabel}
                          </CertificationOptionsTitle>
                          <CertificationOption>
                            <AddonCheckbox>
                              <Checkbox
                                aria-label={
                                  ACCOUNT_MANAGEMENT_MODAL_COPY.selectAllCertificationsLabel
                                }
                                checked={allCertificationsSelected}
                                controlled
                                disabled={isExpired}
                                onChange={toggleAllCertifications}
                                tabIndex={isCertificationOptionsOpen && !isLeaving ? 0 : -1}
                              />
                            </AddonCheckbox>
                            <CertificationDescription>
                              {ACCOUNT_MANAGEMENT_MODAL_COPY.selectAllCertificationsLabel}
                            </CertificationDescription>
                          </CertificationOption>
                          {certificationOptions.map((certification) => (
                            <CertificationOption key={certification.id}>
                              <AddonCheckbox>
                                <Checkbox
                                  aria-label={ACCOUNT_MANAGEMENT_MODAL_COPY.selectCertificationLabel(
                                    certification.code
                                  )}
                                  checked={selectedCertificationIds.includes(certification.id)}
                                  controlled
                                  disabled={isExpired}
                                  onChange={() => toggleCertification(certification.id)}
                                  tabIndex={isCertificationOptionsOpen && !isLeaving ? 0 : -1}
                                />
                              </AddonCheckbox>
                              <CertificationDescription>
                                <CertificationCode>{certification.code}</CertificationCode>{' '}
                                {certification.name}
                              </CertificationDescription>
                            </CertificationOption>
                          ))}
                        </CertificationOptions>
                      )}
                    </AddonEntry>
                  );
                })}
              </AddonList>
            )}
          </Addons>
          <DateSection data-section="start-end-date">
            <DateHeader>
              <DateTitle>{ACCOUNT_MANAGEMENT_MODAL_COPY.startEndDateLabel}</DateTitle>
              <DateFlags>
                <FreeTrial>
                  <AddonCheckbox>
                    <Checkbox
                      aria-label={ACCOUNT_MANAGEMENT_MODAL_COPY.freeTrialLabel}
                      checked={isFreeTrial}
                      controlled
                      disabled={managePlan || isExpired}
                      onChange={toggleFreeTrial}
                    />
                  </AddonCheckbox>
                  <FreeTrialLabel>{ACCOUNT_MANAGEMENT_MODAL_COPY.freeTrialLabel}</FreeTrialLabel>
                </FreeTrial>
                <FreeTrial>
                  <AddonCheckbox>
                    <Checkbox
                      aria-label={ACCOUNT_MANAGEMENT_MODAL_COPY.inviteOnlyLabel}
                      checked={isInviteOnly}
                      controlled
                      disabled={managePlan || isExpired}
                      onChange={() => setIsInviteOnly((value) => !value)}
                    />
                  </AddonCheckbox>
                  <FreeTrialLabel>{ACCOUNT_MANAGEMENT_MODAL_COPY.inviteOnlyLabel}</FreeTrialLabel>
                </FreeTrial>
              </DateFlags>
            </DateHeader>
            <DateFields>
              <DateCard>
                <Icon color="var(--lilac-dark, #7965c6)" icon="Calendar" size="default" />
                <DateCardLabel as="label" htmlFor="account-management-start-date">
                  {ACCOUNT_MANAGEMENT_MODAL_COPY.startDateLabel}
                </DateCardLabel>
                <DatePickerControl>
                  <DatePicker
                    disabled={managePlan}
                    disabledDate={disableBeforeToday}
                    format={DATE_FORMAT}
                    hideIcon
                    id="account-management-start-date"
                    onChange={(date) => updateStartDate(date)}
                    placeholder={DATE_FORMAT.toLowerCase()}
                    value={startDate}
                  />
                </DatePickerControl>
              </DateCard>
              <DateCard>
                <Icon color="var(--lilac-dark, #7965c6)" icon="Calendar" size="default" />
                <DateCardLabel as="label" htmlFor="account-management-end-date">
                  {ACCOUNT_MANAGEMENT_MODAL_COPY.endDateLabel}
                </DateCardLabel>
                <DatePickerControl>
                  <DatePicker
                    disabled={managePlan || !startDate}
                    disabledDate={(date) =>
                      Boolean(startDate && disableSameOrBefore(date, startDate))
                    }
                    format={DATE_FORMAT}
                    hideIcon
                    id="account-management-end-date"
                    onChange={(date) => setEndDate(date)}
                    placeholder={DATE_FORMAT.toLowerCase()}
                    value={endDate}
                  />
                </DatePickerControl>
              </DateCard>
            </DateFields>
          </DateSection>
          <StaffFields>
            <StaffField data-section="support">
              <StaffFieldLabel>{ACCOUNT_MANAGEMENT_MODAL_COPY.supportLabel}</StaffFieldLabel>
              <InputSelect
                onChange={(value) => setSupport(value)}
                options={getStaffOptions(staff, 'Support')}
                placeHolder={ACCOUNT_MANAGEMENT_MODAL_COPY.representativePlaceholder}
                disabled={managePlan || isExpired}
                searchable
                searchLabel={ACCOUNT_MANAGEMENT_MODAL_COPY.representativeFilterLabel}
                value={getStaffName(staff, support) || ''}
              />
            </StaffField>
            <StaffField data-section="sales">
              <StaffFieldLabel>{ACCOUNT_MANAGEMENT_MODAL_COPY.salesLabel}</StaffFieldLabel>
              <InputSelect
                onChange={(value) => setSales(value)}
                options={getStaffOptions(staff, 'Sales')}
                placeHolder={ACCOUNT_MANAGEMENT_MODAL_COPY.representativePlaceholder}
                disabled={managePlan || isExpired}
                searchable
                searchLabel={ACCOUNT_MANAGEMENT_MODAL_COPY.representativeFilterLabel}
                value={getStaffName(staff, sales) || ''}
              />
            </StaffField>
          </StaffFields>
        </Content>
        <Actions
          data-testid="account-management-modal-actions"
          data-section="footer-action"
          onFocus={() => setFocusedSection(null)}
          $isCreateMode={isCreateMode}
        >
          {isExistingAccountMode && (
            <PlanChangeDisclaimer>
              {ACCOUNT_MANAGEMENT_MODAL_COPY.planChangeDisclaimer}
            </PlanChangeDisclaimer>
          )}
          <Button
            $primary
            disabled={isSubmitting}
            htmlType="button"
            loading={isSubmitting}
            onClick={managePlan ? () => handleChangePlan() : () => handleAccountSubmit()}
          >
            {actionLabel}
          </Button>
        </Actions>
        <ConfirmModal
          acceptLabel={ACCOUNT_MANAGEMENT_MODAL_COPY.confirmPlanChangesAcceptLabel}
          cancelLabel={ACCOUNT_MANAGEMENT_MODAL_COPY.confirmPlanChangesCancelLabel}
          description={ACCOUNT_MANAGEMENT_MODAL_COPY.confirmPlanChangesDescription}
          onAccept={() => {
            setIsPlanChangeConfirmationOpen(false);
            void handleAccountSubmit(true);
          }}
          onCancel={() => setIsPlanChangeConfirmationOpen(false)}
          open={isPlanChangeConfirmationOpen}
          title={ACCOUNT_MANAGEMENT_MODAL_COPY.confirmPlanChangesTitle}
        />
      </ModalShell>
    </Modal>
  );
};

export default AccountManagementModal;
